import { Info, UsersThree, ChatCircleText } from "@phosphor-icons/react";
import "./CouplePartnersFields.css";
import "./PatientToggles.css";

const formatPhone = (value) => value.replace(/\D/g, "").slice(0, 10);
const formatIdNumber = (value) => value.replace(/\D/g, "").slice(0, 9);

/**
 * Toggle card (rendered inside a .ptg-grid) for "this patient is a couple".
 * Rendered by CreatePatientPage / PatientProfilePage edit mode when the clinic
 * has clinicManagesCouplesAsPatients enabled (or the patient is already a couple).
 */
export function CoupleToggle({ checked, onChange }) {
  const isOn = checked === true;

  return (
    <label className={`ptg-card ${isOn ? "is-on" : ""}`}>
      <span className="ptg-icon">
        <UsersThree size={17} weight={isOn ? "fill" : "duotone"} />
      </span>
      <span className="ptg-text">
        <span className="ptg-title">מטופל זוגי</span>
        <span className="ptg-sub">שני בני זוג מנוהלים בתיק אחד</span>
      </span>
      <span className="ptg-switch">
        <input
          type="checkbox"
          checked={isOn}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="ptg-slider"></span>
      </span>
    </label>
  );
}

function PartnerSection({
  partnerKey,
  title,
  partner,
  errors,
  onPartnerChange,
  isPrimary,
  onSetPrimary,
}) {
  return (
    <div className={`couple-partner-section ${isPrimary ? "is-primary" : ""}`}>
      <div className="couple-partner-header">
        <h4>{title}</h4>
        <label className="couple-primary-radio">
          <input
            type="radio"
            name="couple-sms-primary"
            checked={isPrimary}
            onChange={onSetPrimary}
          />
          <ChatCircleText size={14} weight="bold" />
          <span>מספר ראשי לקבלת SMS</span>
        </label>
      </div>
      <div className="couple-partner-fields">
        <div className="couple-field-group">
          <label className="required">שם פרטי</label>
          <input
            type="text"
            value={partner.firstName}
            onChange={(e) =>
              onPartnerChange(partnerKey, "firstName", e.target.value)
            }
            className={errors[`${partnerKey}FirstName`] ? "error" : ""}
            placeholder="הכנס שם פרטי"
          />
          {errors[`${partnerKey}FirstName`] && (
            <span className="error-text">
              {errors[`${partnerKey}FirstName`]}
            </span>
          )}
        </div>
        <div className="couple-field-group">
          <label className="required">שם משפחה</label>
          <input
            type="text"
            value={partner.lastName}
            onChange={(e) =>
              onPartnerChange(partnerKey, "lastName", e.target.value)
            }
            className={errors[`${partnerKey}LastName`] ? "error" : ""}
            placeholder="הכנס שם משפחה"
          />
          {errors[`${partnerKey}LastName`] && (
            <span className="error-text">
              {errors[`${partnerKey}LastName`]}
            </span>
          )}
        </div>
        <div className="couple-field-group">
          <label className="required">מספר טלפון</label>
          <input
            type="tel"
            value={partner.phone}
            onChange={(e) =>
              onPartnerChange(partnerKey, "phone", formatPhone(e.target.value))
            }
            className={errors[`${partnerKey}Phone`] ? "error" : ""}
            placeholder="0501234567"
            dir="ltr"
          />
          {errors[`${partnerKey}Phone`] && (
            <span className="error-text">{errors[`${partnerKey}Phone`]}</span>
          )}
        </div>
        <div className="couple-field-group">
          <label>תעודת זהות</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={9}
            value={partner.idNumber || ""}
            onChange={(e) =>
              onPartnerChange(
                partnerKey,
                "idNumber",
                formatIdNumber(e.target.value)
              )
            }
            className={errors[`${partnerKey}Id`] ? "error" : ""}
            placeholder="123456789"
            dir="ltr"
          />
          {errors[`${partnerKey}Id`] && (
            <span className="error-text">{errors[`${partnerKey}Id`]}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Two partner sections + SMS-primary selection for a couple patient.
 * Used by CreatePatientPage and PatientProfilePage (edit mode).
 *
 * Props:
 * - partner1 / partner2: { firstName, lastName, phone, idNumber }
 * - smsPrimaryPartner: 1 | 2
 * - errors: object with keys like partner1FirstName / partner2Phone / partner1Id
 * - onPartnerChange(partnerKey: "partner1"|"partner2", field, value)
 * - onPrimaryChange(1|2)
 */
export default function CouplePartnersFields({
  partner1,
  partner2,
  smsPrimaryPartner,
  errors = {},
  onPartnerChange,
  onPrimaryChange,
}) {
  const primary = smsPrimaryPartner === 2 ? 2 : 1;
  return (
    <div className="couple-partners-fields">
      <PartnerSection
        partnerKey="partner1"
        title="בן/בת זוג 1"
        partner={partner1}
        errors={errors}
        onPartnerChange={onPartnerChange}
        isPrimary={primary === 1}
        onSetPrimary={() => onPrimaryChange(1)}
      />
      <PartnerSection
        partnerKey="partner2"
        title="בן/בת זוג 2"
        partner={partner2}
        errors={errors}
        onPartnerChange={onPartnerChange}
        isPrimary={primary === 2}
        onSetPrimary={() => onPrimaryChange(2)}
      />
      <div className="couple-sms-explanation">
        <Info size={14} weight="bold" />
        <span>
          המספר המסומן כראשי ישמש לשליחת הודעות SMS אוטומטיות: אישורי הגעה
          לפגישות, תזכורות לטיפולים, וקישורים למילוי טפסים ושאלונים דיגיטליים.
        </span>
      </div>
    </div>
  );
}
